import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import { useAnalytics } from 'use-analytics';
import { useCallback, useMemo, useEffect } from 'react';
import MultiSelectAutocompleteFilter from '../../../../Filters/MultiSelectAutocompleteFilter';
import Dropdown from '../../../../Dropdown';
import Filter from '../../../../Filters/Filter';
import flightSearchInput from '../../../../../helpers/selectors/flightSearchInput';
import { setFlightTripSearchInputs } from '../../../../../store/slices/tripSearch';
import { updateFlightSearchObj } from '../../../../../store/slices/flight';
import { autocompleteRequest } from '../../../../../store/slices/autocomplete';
import {
  AutocompleteType,
  FlightSearchTypes,
  connectingAirportsOptions,
} from '../../../../../constants';

import Checkbox from '../../../../Checkbox';
import DropdownSelectFilter from '../../../../Filters/DropdownSelectFilter';

const ConnectingAirports = ({ tripId, filterBgColor }) => {
  const dispatch = useDispatch();
  const { track } = useAnalytics();
  const isTripSearch = !tripId;

  const { airportAutocompleteResults } = useSelector((store) => store.autocomplete);
  const { searchInputs } = useSelector(flightSearchInput(tripId, isTripSearch));

  const isEmptyFilter = useMemo(
    () =>
      !_.find(
        searchInputs.flightSegments,
        (fs) => fs.connectingAirports && fs.connectingAirports.length > 0,
      ),
    [searchInputs.flightSegments],
  );

  const handleChange = (newValue) => {
    const payload = {
      tripId,
      searchObj: {
        flightSegments: searchInputs.flightSegments ? [...searchInputs.flightSegments] : [],
      },
    };
    const segments = payload.searchObj.flightSegments;
    const isMultiCityOrRoundTrip = [
      FlightSearchTypes.MULTI_CITY,
      FlightSearchTypes.ROUND_TRIP,
    ].includes(searchInputs.flightSearchType);

    const updateSegment = (index) => {
      const connectingPref =
        newValue.connectingAirportsPreference ?? segments[index].connectingAirportsPreference ?? 0;

      segments[index] = {
        ...segments[index],
        ..._.omit(newValue, 'rowIndex'),
        connectingAirportsPreference: connectingPref,
      };
    };

    if (searchInputs.flightSearchType === FlightSearchTypes.ONE_WAY) {
      updateSegment(0);
    } else if (isMultiCityOrRoundTrip) {
      if (searchInputs.hasDifferentOutboundInboundConnectingAirports) {
        updateSegment(newValue.rowIndex);
      } else {
        segments.forEach((s, index) => updateSegment(index));
      }
    }

    if (isTripSearch) dispatch(setFlightTripSearchInputs({ ...payload.searchObj }));
    else dispatch(updateFlightSearchObj(payload));
    track('ConnectingAirports.handleChange', {
      ...newValue,
    });
  };

  const handleHasDifferentOutboundInboundConnectingAirports = useCallback(
    (newValue) => {
      const payload = {
        tripId,
        searchObj: {
          ...newValue,
          flightSegments: searchInputs.flightSegments ? [...searchInputs.flightSegments] : [],
        },
      };
      payload.searchObj.flightSegments.forEach((segment, index) => {
        payload.searchObj.flightSegments[index] = {
          ...payload.searchObj.flightSegments[index],
          connectingAirports: '',
          connectingAirportsPreference: 0,
        };
      });
      if (isTripSearch) dispatch(setFlightTripSearchInputs({ ...payload.searchObj }));
      else dispatch(updateFlightSearchObj(payload));
      track('ConnectingAirports.handleHasDifferentOutboundInboundConnectingAirports', {
        ...newValue,
      });
    },
    [dispatch, isTripSearch, searchInputs.flightSegments, track, tripId],
  );

  const handleClearFilter = useCallback(() => {
    handleHasDifferentOutboundInboundConnectingAirports({
      hasDifferentOutboundInboundConnectingAirports: false,
    });

    track('filterBar.clearFilter');
  }, [handleHasDifferentOutboundInboundConnectingAirports, track]);

  useEffect(() => {
    if (
      searchInputs.flightSearchType === FlightSearchTypes.ONE_WAY &&
      searchInputs.hasDifferentOutboundInboundConnectingAirports
    ) {
      handleHasDifferentOutboundInboundConnectingAirports({
        hasDifferentOutboundInboundConnectingAirports: false,
      });
    }
  }, [searchInputs, handleHasDifferentOutboundInboundConnectingAirports]);

  const handleInputChange = useCallback(
    (query) => {
      if (query && query.length < 2) return;
      dispatch(
        autocompleteRequest({
          autocompleteTypes: AutocompleteType.AIRPORTS,
          responseType: 'airportAutocompleteResults',
          query,
        }),
      );
    },
    [dispatch],
  );

  const options = connectingAirportsOptions;
  const airportsInputs = (
    connectingAirportsPreferenceProperty,
    connectingAirports,
    flightSegmetRowIndex = 0,
  ) => {
    const connectingAirportsPreference =
      searchInputs &&
      searchInputs.flightSegments &&
      searchInputs.flightSegments[flightSegmetRowIndex][connectingAirportsPreferenceProperty];
    const matchingOption = options.find((o) => o.value === connectingAirportsPreference);
    const labels = matchingOption ? matchingOption.label : options[0].label;

    return (
      <div className="flex font-normal">
        <DropdownSelectFilter
          onChange={(newValue) =>
            handleChange({
              [connectingAirportsPreferenceProperty]: newValue,
              rowIndex: flightSegmetRowIndex,
            })
          }
          label={labels}
          options={options}
          selectedOptionValue={
            searchInputs.flightSegments &&
            searchInputs.flightSegments[flightSegmetRowIndex] &&
            searchInputs.flightSegments[flightSegmetRowIndex][connectingAirportsPreferenceProperty]
              ? searchInputs.flightSegments[flightSegmetRowIndex][
                  connectingAirportsPreferenceProperty
                ]
              : options[0].value
          }
          defaultOptionValue={
            (searchInputs.flightSegments &&
              searchInputs.flightSegments[flightSegmetRowIndex] &&
              searchInputs.flightSegments[flightSegmetRowIndex][
                connectingAirportsPreferenceProperty
              ]) ||
            options[0].value
          }
          menuWidth="w-64"
          filterButtonMaxWidth="max-w-full w-32"
          filterSpacingClass="pl-2 py-2 pr-2"
          variant="outline-left-radius"
          fontSize="font-normal"
        />
        <MultiSelectAutocompleteFilter
          onChange={(newValue) =>
            handleChange({
              [connectingAirports]: newValue,
              rowIndex: flightSegmetRowIndex,
            })
          }
          label="Connecting Airports"
          tooltipPrependText=""
          options={airportAutocompleteResults}
          filterMaxWidthClass="max-w-full w-56"
          onInputChange={handleInputChange}
          filterSpacingClass="px-4 py-2"
          values={
            searchInputs?.flightSegments?.[flightSegmetRowIndex]?.connectingAirports ||
            searchInputs.connectingAirports
          }
          bgClassName={!isTripSearch ? '' : filterBgColor}
          variant="outline-right-radius"
        />
      </div>
    );
  };

  return (
    <Filter
      filterMaxWidthClass="max-w-full"
      label="Connecting Airports"
      menuWidthClass="w-max"
      isFilterEmpty={isEmptyFilter}
      handleClear={handleClearFilter}
      variant={isTripSearch ? undefined : 'outline'}
      bgClassName={!isTripSearch ? '' : filterBgColor}
    >
      <Dropdown.Item withGridLayout={false}>
        {searchInputs.flightSearchType !== FlightSearchTypes.ONE_WAY ? (
          <Checkbox
            checked={!!searchInputs.hasDifferentOutboundInboundConnectingAirports}
            onChange={(event, newValue) =>
              handleHasDifferentOutboundInboundConnectingAirports({
                hasDifferentOutboundInboundConnectingAirports: newValue,
              })
            }
            label={
              searchInputs.flightSearchType === FlightSearchTypes.ROUND_TRIP
                ? 'Different outbound/inbound connecting airports'
                : 'Different connecting airports for all flights'
            }
          />
        ) : (
          <></>
        )}
        {searchInputs.flightSearchType === FlightSearchTypes.ONE_WAY &&
          airportsInputs('connectingAirportsPreference', 'connectingAirports')}
        {!searchInputs.hasDifferentOutboundInboundConnectingAirports &&
          searchInputs.flightSearchType !== FlightSearchTypes.ONE_WAY &&
          airportsInputs('connectingAirportsPreference', 'connectingAirports')}

        {searchInputs.hasDifferentOutboundInboundConnectingAirports &&
        searchInputs.flightSearchType === FlightSearchTypes.ROUND_TRIP ? (
          <>
            <div className="mt-2 font-bold">Outbound:</div>
            {airportsInputs('connectingAirportsPreference', 'connectingAirports', 0)}
            <div className="mt-5 font-bold">Inbound:</div>
            {airportsInputs('connectingAirportsPreference', 'connectingAirports', 1)}
          </>
        ) : (
          <></>
        )}

        {searchInputs.hasDifferentOutboundInboundConnectingAirports &&
          searchInputs.flightSearchType === FlightSearchTypes.MULTI_CITY &&
          searchInputs.flightSegments.map((flightSegment, currentIndex) => (
            <>
              <div key={`${currentIndex.toString()}`} className="mt-4 font-bold">
                Flight
                <span className="mx-1">{currentIndex + 1}:</span>
              </div>
              {airportsInputs('connectingAirportsPreference', 'connectingAirports', currentIndex)}
            </>
          ))}
      </Dropdown.Item>
    </Filter>
  );
};

ConnectingAirports.propTypes = {
  tripId: PropTypes.string,
  filterBgColor: PropTypes.string,
};

ConnectingAirports.defaultProps = {
  tripId: undefined,
  filterBgColor: undefined,
};

export default ConnectingAirports;
